import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardIcon,
  ClipboardDocumentCheckIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps } from '../../types';

type InstanceOption = 'DEV' | 'TEST' | 'PROD';

interface CredentialFormData {
  apiUrl: string;
  clientId?: string;
  companyId: string;
  username?: string;
  instance: InstanceOption;
}

const CredentialForm: React.FC<WizardComponentProps> = ({ isEmbedded, onFailure }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<CredentialFormData>({
    apiUrl: 'https://api2preview.sapsf.eu/',
    clientId: '',
    companyId: '',
    username: '',
    instance: 'DEV'
  });

  // no secret fields in this simplified flow
  const [certDetails, setCertDetails] = useState<{ raw?: string } | null>(null);
  const [certError, setCertError] = useState<string | null>(null);
  const [isCertAccordionOpen, setIsCertAccordionOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Fetch certificate details
  useEffect(() => {
    apiClient.getCertificate()
      .then(data => {
        setCertDetails(data);
        setCertError(null);
      })
      .catch(err => {
        console.error('Failed to fetch certificate details', err);
        setCertError('Certificate not found or server error. Please contact your administrator.');
      });
  }, []);

  const handleCopyToClipboard = () => {
    if (certDetails?.raw) {
      navigator.clipboard.writeText(certDetails.raw);
      setIsCopied(true);
      toast.success('Public key copied to clipboard');
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  const [testResult, setTestResult] = useState<{
    success?: boolean;
    message?: string;
    timestamp?: string;
  } | null>(null);


  // Fetch existing credentials
  const { data: existingCredentials } = useQuery({
    queryKey: ['credentials', customerId, phaseId],
    queryFn: () => apiClient.get(`/credentials/${customerId}/${phaseId}`).then(res => res.data),
    enabled: !!(customerId && phaseId),
    retry: false
  });
  console.log('Fetching existing credentials...');
  console.log('Customer ID:', customerId);
  console.log('Phase ID:', phaseId);
  console.log('Enabled:', !!(customerId && phaseId));
  console.log('Existing credentials:', existingCredentials);
  useEffect(() => {
    if (existingCredentials) {
      setFormData({
        apiUrl: existingCredentials.apiUrl || 'https://api2preview.sapsf.eu/',
        clientId: existingCredentials.clientId || '',
        companyId: existingCredentials.companyId || '',
        username: existingCredentials.username || '',
        instance: existingCredentials.instance || 'DEV'
      });
    }
  }, [existingCredentials]);

  // Save credentials mutation
  const saveCredentialsMutation = useMutation({
    mutationFn: (data: CredentialFormData) => apiClient.saveCredentials(customerId!, phaseId!, data),
    onSuccess: () => {
      toast.success('Credentials saved successfully');
      queryClient.invalidateQueries({ queryKey: ['credentials', customerId, phaseId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to save credentials');
    }
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: () => apiClient.testCredentials(customerId!, phaseId!),
    onSuccess: (result) => {
      setTestResult(result);
      if (result.success) {
        toast.success('SuccessFactors connection test successful');
      } else {
        toast.error('SuccessFactors connection test failed');
      }
    },
    onError: (error: any) => {
      setTestResult({
        success: false,
        message: error.response?.data?.error?.message || 'Connection test failed',
        timestamp: new Date().toISOString()
      });
      toast.error('SuccessFactors connection test failed');
    }
  });

  // Phase SharePoint status
  const { data: phaseSharePoint, refetch: refetchPhaseSp } = useQuery({
    queryKey: ['phase-sharepoint', customerId, phaseId],
    queryFn: () => apiClient.getPhaseSharePoint(customerId!, phaseId!),
    enabled: !!(customerId && phaseId)
  });

  // Check for OAuth callback status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const spStatus = urlParams.get('sp');
    const spError = urlParams.get('oauth_error');

    if (spStatus === 'connected') {
      toast.success('SharePoint connected successfully!');
      // Clear param
      urlParams.delete('sp');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);
      refetchPhaseSp();
    } else if (spError) {
      const errorMessage = `SharePoint connection failed: ${decodeURIComponent(spError)}`;

      // Clear param immediately
      urlParams.delete('oauth_error');
      const newSearch = urlParams.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? '?' + newSearch : ''}`);

      if (onFailure) {
        onFailure(errorMessage);
      } else {
        toast.error(errorMessage);
        navigate(`/customers/${customerId}/phases/${phaseId}`);
      }
    }
  }, [onFailure, customerId, phaseId, navigate, refetchPhaseSp]);

  const connectPhaseSpMutation = useMutation({
    mutationFn: () => apiClient.connectPhaseSharePoint(customerId!, phaseId!, {}),
    onSuccess: async () => {
      toast.success('SharePoint connected for this phase');
      await refetchPhaseSp();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Failed to connect SharePoint';
      if (onFailure) {
        onFailure(msg);
      } else {
        toast.error(msg);
        navigate(`/customers/${customerId}/phases/${phaseId}`);
      }
    }
  });

  const copyPhaseSpMutation = useMutation({
    mutationFn: (payload: { fromCustomerId: string; fromPhaseId: string }) =>
      apiClient.copyPhaseSharePoint(customerId!, phaseId!, payload.fromCustomerId, payload.fromPhaseId),
    onSuccess: async () => {
      toast.success('SharePoint connection copied');
      await refetchPhaseSp();
    },
    onError: (error: any) => {
      const msg = error.response?.data?.error?.message || 'Failed to copy SharePoint connection';
      if (onFailure) {
        onFailure(msg);
      } else {
        toast.error(msg);
        navigate(`/customers/${customerId}/phases/${phaseId}`);
      }
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveCredentialsMutation.mutate(formData);
  };

  const handleTestConnection = async () => {
    if (formData.apiUrl && formData.companyId && formData.instance) {
      await saveCredentialsMutation.mutateAsync(formData);
      testConnectionMutation.mutate();
    } else {
      toast.error('Please fill in API URL, Company ID and Instance before testing');
    }
  };

  const [copyFromCustomerId, setCopyFromCustomerId] = useState('');
  const [copyFromPhaseId, setCopyFromPhaseId] = useState('');
  const [spSiteUrl, setSpSiteUrl] = useState('');
  const [spAppId, setSpAppId] = useState('');
  const [spTenantId, setSpTenantId] = useState('');
  const [siteSearch, setSiteSearch] = useState('');
  const [siteResults, setSiteResults] = useState<Array<{ id: string; name: string; displayName?: string; webUrl: string }>>([]);
  const [searchingSites, setSearchingSites] = useState(false);

  const handleInputChange = (field: keyof CredentialFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setTestResult(null); // Clear test result when form changes
  };

  // no secret toggle needed

  return (
    <div className="max-w-4xl mx-auto">
      {!isEmbedded && (
        <>
          {/* Breadcrumb Navigation */}
          <nav className="mb-4 flex items-center text-sm text-gray-600">
            <Link to="/customers" className="hover:text-primary-600 transition-colors">
              Customers
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases`}
              className="hover:text-primary-600 transition-colors"
            >
              Phases
            </Link>
            <span className="mx-2">/</span>
            <Link
              to={`/customers/${customerId}/phases/${phaseId}`}
              className="hover:text-primary-600 transition-colors"
            >
              Phase Dashboard
            </Link>
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">Credentials</span>
          </nav>

          {/* Back Button */}
          <Link
            to={`/customers/${customerId}/phases/${phaseId}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4 mr-1" />
            Back to Phase Dashboard
          </Link>

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Migration Credentials</h1>
            <p className="text-gray-600 mt-2">Configure credentials for SuccessFactors source and SharePoint target connections</p>
          </div>
        </>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <h2 className="text-lg font-semibold text-blue-900 mb-4">How to Setup SuccessFactors OAuth?</h2>
        <div className="prose prose-blue max-w-none text-sm text-blue-800">
          <ol className="list-decimal pl-5 space-y-2">
            <li>Log in to your SuccessFactors instance (Admin Center).</li>
            <li>Search for and navigate to <strong>Manage OAuth2 Client Applications</strong>.</li>
            <li>Click <strong>Register Client Application</strong>.</li>
            <li>Fill in <strong>Application Name</strong> (e.g., "Migration App") and <strong>Description</strong>.</li>
            <li>In the <strong>Application URL</strong> field, enter your application URL (e.g., <code>{window.location.origin}</code>).</li>
            <li>
              Register the <strong>X.509 Certificate</strong> in SuccessFactors by pasting the public key.
              <div className="mt-4 border border-blue-200 rounded-lg overflow-hidden bg-white">
                <button
                  type="button"
                  onClick={() => setIsCertAccordionOpen(!isCertAccordionOpen)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-blue-100 text-blue-900 hover:bg-blue-200 transition-colors focus:outline-none"
                >
                  <span className="font-semibold">Certificate Details</span>
                  {isCertAccordionOpen ? (
                    <ChevronUpIcon className="w-5 h-5 text-blue-700" />
                  ) : (
                    <ChevronDownIcon className="w-5 h-5 text-blue-700" />
                  )}
                </button>

                {isCertAccordionOpen && (
                  <div className="p-4 bg-white">
                    <div className="flex flex-col space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Public Key (Raw Base64)</span>
                        <button
                          type="button"
                          onClick={handleCopyToClipboard}
                          className="inline-flex items-center text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          {isCopied ? (
                            <ClipboardDocumentCheckIcon className="w-4 h-4 mr-1 text-green-600" />
                          ) : (
                            <ClipboardIcon className="w-4 h-4 mr-1" />
                          )}
                          {isCopied ? 'Copied!' : 'Copy to Clipboard'}
                        </button>
                      </div>
                      <div className="relative">
                        {certError ? (
                          <div className="w-full p-4 bg-red-50 border border-red-200 rounded-md text-red-700 text-xs">
                            {certError}
                          </div>
                        ) : (
                          <textarea
                            readOnly
                            value={certDetails?.raw || 'Loading...'}
                            className="w-full h-32 p-3 text-xs font-mono bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-0 resize-none overflow-y-auto break-all"
                          />
                        )}
                      </div>
                      <div className="flex justify-start">
                        <a
                          href="/api/credentials/certificate"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                          Still need the .cer file? Download here
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </li>
            <li>Save the registration.</li>
            <li>Copy the <strong>API Key</strong> (Client ID) generated and paste it into the form below.</li>
          </ol>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* API URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              API URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={formData.apiUrl}
              onChange={(e) => handleInputChange('apiUrl', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="https://api2preview.sapsf.eu/"
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              The base URL for your SuccessFactors API endpoint
            </p>
          </div>

          {/* Company ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.companyId}
              onChange={(e) => handleInputChange('companyId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Your company identifier"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Client ID (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={formData.clientId}
                onChange={(e) => handleInputChange('clientId', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Optional"
              />
            </div>

            {/* Instance */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instance <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.instance}
                onChange={(e) => handleInputChange('instance', e.target.value as InstanceOption)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="DEV">DEV</option>
                <option value="TEST">TEST</option>
                <option value="PROD">PROD</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Username (Optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username (Optional)
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Service user username"
              />
            </div>
          </div>

        </form>

        <div className="mt-6 pt-6 border-t border-gray-200 flex flex-col space-y-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={(e) => handleSubmit(e)}
              disabled={saveCredentialsMutation.isPending}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {saveCredentialsMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </button>
            <button
              onClick={handleTestConnection}
              disabled={testConnectionMutation.isPending || saveCredentialsMutation.isPending}
              type="button"
              className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {testConnectionMutation.isPending ? 'Testing...' : 'Test Connection'}
            </button>
          </div>

          {testResult && (
            <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {testResult.success ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-400" aria-hidden="true" />
                  ) : (
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
                  )}
                </div>
                <div className="ml-3">
                  <h3 className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                    {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                  </h3>
                  <div className={`mt-2 text-sm ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    <p>{testResult.message}</p>
                    {testResult.timestamp && (
                      <p className="mt-1 text-xs opacity-75">
                        Tested at: {new Date(testResult.timestamp).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CredentialForm;
